<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
// Overrides @basecom-gmbh/pond/components/Account/AccountRegister.vue
import type { FormkitFields } from '@basecom-gmbh/pond/types/formkit';
import { ApiClientError } from '@shopware/api-client';
import AddressFormFields from '../Address/AddressFormFields.vue';
import { inject } from 'vue';

const props = withDefaults(
    defineProps<{
        redirectAfterSuccess?: boolean;
        redirectTarget?: string;
        showCreateLink?: boolean;
        allowGuest?: boolean;
        isOnCheckout?: boolean;
    }>(),
    {
        redirectAfterSuccess: false,
        redirectTarget: '/account',
        showCreateLink: true,
        allowGuest: false,
        isOnCheckout: false,
    },
);

const customerStore = useCustomerStore();
const { pushError, pushSuccess } = useNotifications();
const formErrorStore = useFormErrorStore();
const { trackRegister } = useAnalytics();
const { t } = useI18n();

const isLoading = ref(false);
const isGuest = ref(false);
const registrationData = ref({});

//CUSTOM IDS-168 inject registration trigger from pages/checkout/confirm.vue
const registerUser = inject<null | { value: (fields) => Promise<void> }>('registerUser', null);

const handleRegisterSubmit = async (formData?: FormkitFields) => {
    const fields = formData || registrationData.value;
    isLoading.value = true;

    let userData: null;
    // CUSTOM IDS-49: Changed the whole structure of userData that is being sent in order to accommodate the new registration requirements
    const baseUserData = {
        accountType: 'business',
        guest: isGuest.value,
        salutationId: fields.salutationId,
        requestedGroupId: fields.requestedGroupId,
        firstName: fields.firstName,
        lastName: fields.lastName,
        vatIds: [fields.vatIds],
        billingAddress: {
            phoneNumber: fields.billingAddress.phoneNumber,
            company: fields.company,
            street: fields.billingAddress.street,
            zipcode: fields.billingAddress.zipcode,
            city: fields.billingAddress.city,
            countryId: fields.billingAddress.countryId,
            country: {
                vatIdRequired: true,
                vatIdPattern: '/^\\d+$/',
            },
        },
        email: fields.email,
        password: fields.password,
    };

    if (fields.alternativeShippingAddress.showAlternativeShippingAddress) {
        userData = {
            ...baseUserData,
            shippingAddress: {
                ...fields.alternativeShippingAddress,
                ...fields.alternativeShippingAddress.shippingAddress,
            },
        };
    } else {
        userData = {
            ...baseUserData,
        };
    }

    try {
        await customerStore.register({
            ...userData,
        });
        isLoading.value = false;

        trackRegister();

        if (props.redirectAfterSuccess) {
            navigateTo(props.redirectTarget);
        }

        if (props.allowGuest && customerStore.customer.guest) {
            pushSuccess(t('account.register.guest.sessionStarted'));
        } else {
            pushSuccess(t('account.register.success'));
        }
    } catch (error) {
        pushError(t('account.register.error'));
        isLoading.value = false;

        if (error instanceof ApiClientError) {
            formErrorStore.formErrors(error.details.errors);
            return;
        }

        formErrorStore.apiErrors.value.push({ key: 'register', code: 'REGISTER_GENERAL_ERROR' });
    }
};

if (registerUser) {
    registerUser.value = handleRegisterSubmit;
}
</script>

<!--CUSTOM IDS-104: Moved all input field to AddressFormFields. Changed styles, labels, translations.-->
<template>
    
<FormKitLazyProvider config-file="true">
<FormKit
        v-model="registrationData"
        type="form"
        :submit-label="$t('account.register.submitLabel')"
        :classes="{
            form: 'grid grid-cols-2 gap-4 w-full',
            messages: 'hidden',
        }"
        :config="{
            validationVisibility: 'dirty',
        }"
        :actions="false"
        @submit="handleRegisterSubmit"
    >
        <ul
            v-if="formErrorStore.apiErrors.filter(error => error.key === 'register').length"
            class="validation-errors text-status-danger"
        >
            <li
                v-for="(error, index) in formErrorStore.apiErrors.filter(error => error.key === 'register')"
                :key="`login-error-${index}`"
            >
                {{ error.code }}
            </li>
        </ul>

        <AddressFormFields
            v-model:is-guest="isGuest"
            :allow-guest="allowGuest"
            :in-checkout="true"
            :requestInitialVat="true"
        />

        <FormKit
            v-slot="{ value }"
            type="group"
            name="alternativeShippingAddress"
            :classes="{
                outer: {
                    'col-span-2': true,
                },
            }"
        >
            <FormKit
                type="checkbox"
                :label="$t('account.register.alternativeShippingAddress')"
                name="showAlternativeShippingAddress"
                :value="false"
                :classes="{
                    outer: {
                        'col-span-2': true,
                    },
                }"
            />

            <div class="col-span-2" />

            <template v-if="value.showAlternativeShippingAddress === true">
                <div class="col-span-2">
                    <span class="pt-8 text-left text-lg font-extrabold">{{
                        $t('account.register.shippingAddressHeading')
                    }}</span>
                </div>
                <AddressFormFields
                    address-type="shippingAddress"
                    :in-checkout="true"
                />
                <div class="col-span-2" />
            </template>
        </FormKit>

        <template v-if="!isOnCheckout">
            <div class="col-span-2">
                <span class="non-italic text-xl font-extrabold"> {{ $t('account.register.terms.heading') }} </span>
            </div>

            <!-- Custom IDS-132 added v-model,deleted @click -->
            <FormKit
                type="checkbox"
                name="terms"
                :value="false"
                decorator-icon="check"
                validation="accepted"
                :classes="{
                    inner: 'self-start mt-1',
                    messages: 'text-red-500 mt-1',
                    outer: 'col-span-2',
                }"
                :validation-messages="{
                    accepted: $t('account.register.terms.help'),
                }"
            >
                <!-- eslint-disable vue/no-v-html -->
                <template #label>
                    <label
                        class="terms-and-conditions"
                        for="terms"
                        v-html="$t('account.register.terms.termsAndConditions')"
                    />
                </template>
            </FormKit>
            <FormKit
                type="submit"
                :classes="{
                    outer: 'col-span-2 relative flex justify-end',
                }"
                :disabled="isLoading"
            >
                <span
                    class="formkit-label"
                    :class="{
                        'opacity-0': isLoading,
                    }"
                >
                    {{ $t('account.register.submitLabel') }}
                </span>
                <UtilityLoadingSpinner
                    v-if="isLoading"
                    size="small"
                />
            </FormKit>
        </template>
    </FormKit>
</FormKitLazyProvider>

</template>
